/**
 * Command Spotlight
 *
 * Modül landing dilinde global komut paleti.
 */

import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";
import {
  PaletteFooter,
  PaletteKbd,
  PaletteShell,
} from "@/components/overlays/palette-shell";
import { CommandItem } from "./CommandItem";
import { CommandLoader } from "./CommandLoader";
import { useCommandSuggestions } from "../hooks/use-command-suggestions";
import { useSlashCommandStore } from "../store/slash-command-store";
import { useCommandExecutor } from "../hooks/use-command-executor";

export const CommandSpotlight: React.FC = () => {
  const { t } = useTranslation();
  const {
    isSpotlightOpen,
    closeSpotlight,
    spotlightQuery,
    setSpotlightQuery,
    selectedIndex,
    setSelectedIndex,
  } = useSlashCommandStore();

  // Spotlight'ta query yoksa tüm komutları göster
  const queryForSuggestions = spotlightQuery.trim() || "/";
  const { suggestions, isLoading, error, hasResults } =
    useCommandSuggestions(queryForSuggestions);
  const { executeCommand } = useCommandExecutor();
  const inputRef = useRef<HTMLInputElement>(null);
  const itemRefs = useRef<Map<number, HTMLButtonElement>>(new Map());

  useEffect(() => {
    if (isSpotlightOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isSpotlightOpen]);

  useEffect(() => {
    if (!isSpotlightOpen || selectedIndex < 0 || !hasResults) return;

    const selectedItem = itemRefs.current.get(selectedIndex);
    if (selectedItem) {
      selectedItem.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [selectedIndex, isSpotlightOpen, hasResults]);

  const setItemRef = (index: number) => (element: HTMLButtonElement | null) => {
    if (element) {
      itemRefs.current.set(index, element);
    } else {
      itemRefs.current.delete(index);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "Escape":
        e.preventDefault();
        closeSpotlight();
        break;

      case "ArrowDown":
        e.preventDefault();
        if (suggestions.length > 0) {
          setSelectedIndex((selectedIndex + 1) % suggestions.length);
        }
        break;

      case "ArrowUp":
        e.preventDefault();
        if (suggestions.length > 0) {
          setSelectedIndex(
            selectedIndex === 0 ? suggestions.length - 1 : selectedIndex - 1,
          );
        }
        break;

      case "Enter":
      case "Tab":
        e.preventDefault();
        if (suggestions.length > 0 && suggestions[selectedIndex]) {
          handleSelect(suggestions[selectedIndex]);
        }
        break;
    }
  };

  const handleSelect = async (command: any) => {
    const result = await executeCommand(command);
    if (result.success) {
      if (command.type === "navigation" || command.type === "action") {
        closeSpotlight();
      } else if (command.type === "parent" || command.type === "dynamic") {
        setSpotlightQuery(command.trigger);
        setSelectedIndex(0);
      }
    }
  };

  if (!isSpotlightOpen) return null;

  const spotlightContent = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10000] flex items-start justify-center px-4 pt-[20vh]"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            closeSpotlight();
          }
        }}
      >
        <button
          type="button"
          onClick={closeSpotlight}
          className="fixed inset-0 bg-black/45 backdrop-blur-md dark:bg-black/60"
          aria-label={t("common.close")}
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: -16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: -16 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
          className="relative w-full max-w-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <PaletteShell
            footer={
              <PaletteFooter>
                <span className="inline-flex items-center gap-1.5">
                  <PaletteKbd>↑↓</PaletteKbd>
                  {t("common.navigate")}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <PaletteKbd>↵</PaletteKbd>
                  {t("common.select")}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <PaletteKbd>esc</PaletteKbd>
                  {t("common.close")}
                </span>
              </PaletteFooter>
            }
          >
            <div className="flex items-center gap-3 border-b border-module-accent/10 px-4 py-3.5 dark:border-white/[0.06]">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-module-accent-soft text-module-accent">
                <Search className="size-4" />
              </span>
              <input
                ref={inputRef}
                type="text"
                value={spotlightQuery}
                onChange={(e) => setSpotlightQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t("slashCommands.searchPlaceholder")}
                className="h-9 flex-1 bg-transparent text-base text-text-primary placeholder:text-text-tertiary focus:outline-none dark:text-gray-100 dark:placeholder:text-gray-500"
                autoFocus
              />
              <button
                type="button"
                onClick={closeSpotlight}
                className="flex size-8 shrink-0 items-center justify-center rounded-full text-text-tertiary transition-colors hover:bg-module-accent-soft hover:text-module-accent dark:text-gray-400"
                aria-label={t("common.close")}
              >
                <X className="size-4" />
              </button>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {hasResults && !isLoading && (
                <div className="flex flex-col gap-1 p-2">
                  {suggestions.map((command, index) => (
                    <CommandItem
                      key={command.id}
                      ref={setItemRef(index)}
                      command={command}
                      onSelect={() => handleSelect(command)}
                      isSelected={index === selectedIndex}
                    />
                  ))}
                </div>
              )}

              {isLoading && <CommandLoader />}

              {error && (
                <div className="px-4 py-8 text-center text-sm text-destructive dark:text-red-400">
                  {error}
                </div>
              )}

              {!isLoading &&
                !hasResults &&
                !error &&
                spotlightQuery.length > 0 && (
                  <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
                    <span className="flex size-11 items-center justify-center rounded-2xl bg-module-accent-soft text-module-accent">
                      <Search className="size-5" />
                    </span>
                    <p className="text-sm text-text-tertiary dark:text-gray-400">
                      {t("slashCommands.noResults")}
                    </p>
                  </div>
                )}
            </div>
          </PaletteShell>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return typeof window !== "undefined"
    ? createPortal(spotlightContent, document.body)
    : null;
};
